import React from "react";
import { MenuItem, TextField } from "@mui/material";
import { AppTheme } from "../../theme/App.theme";

/**
 * @typedef {import("@mui/material").TextFieldProps | AdditionalProps} MUITextFieldProps
 */

/**
 * @typedef {Object} AdditionalProps
 * @property {Array<{value: string, label: string, id: string}>} options - An array of options for select fields.
 */

/**
 * MUITextField is a Material-UI TextField component with custom styles.
 * It can be used to create input fields in forms with consistent styling.
 * @param {MUITextFieldProps} props - The properties to pass to the TextField component.
 * @returns {JSX.Element} A styled TextField component.
 */

const MUITextField = (props) => {
  const defaultInputProps = {
    sx: {
      borderRadius: '10px',
      backgroundColor: props?.disabled && AppTheme.DISABLED_FIELD_COLOR,
      "&.MuiOutlinedInput-root": {
        "& fieldset": {
          borderColor: !props?.error && AppTheme.BORDER_COLOR,
        },
        "&:hover fieldset": {
          borderColor: !props?.error && AppTheme.BORDER_COLOR,
        },
        "&.Mui-focused fieldset": {
          borderColor: !props?.error && AppTheme.BORDER_COLOR,
        },
      },
    },
  };

  /**@type {MUITextFieldProps} - Custom props*/
  const customProps = {
    size: "small",
    variant: "outlined",
    fullWidth: true,
    autoComplete: "off",
    InputProps: {
      ...defaultInputProps,
    },
    ...props,
  };

  return (
    <React.Fragment>
      {customProps?.type === "select" ? (
        <TextField {...customProps} select
          slotProps={{
            select: {
              displayEmpty: true
            }
          }}
        >
          {props?.placeholder && (
            <MenuItem value="" disabled style={{ textWrap: "wrap" }}>
              {props?.placeholder || "Select"}
            </MenuItem>
          )}
          {customProps?.options.map((type, index) => (
            <MenuItem
              id={type.id}
              key={index}
              value={type.value}
              style={{ textWrap: "wrap" }}
              disabled={type.disabled || false}
            >
              {type.label}
            </MenuItem>
          ))}
        </TextField>
      ) : (
        <TextField 
          {...customProps}
          slotProps={{
            input: {
              sx: defaultInputProps.sx,
              startAdornment: props?.starticon || null,
              endAdornment: props?.endicon || null
            }
          }}
        />
      )}
    </React.Fragment>
  );
};

export default MUITextField;
